'use client';
import { memo, useState, useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { Star, PlayCircle, Zap, Clock, ShieldCheck } from 'lucide-react';
import { FlipText } from './ui/flip-text';
import { PixelButton } from '@/components/PixelButton';

// Deferred: keeps three.js out of the hero's critical bundle. The shader is a
// decorative backdrop and is skipped entirely on low-end devices anyway.
const Mosaic = dynamic(() => import('@/components/MagicGrid'), { ssr: false });

// --- Module-scope constants (created once, never re-allocated) ---

/** Hoisted outside component tree — avoids recreation on every GlassButton render */
const MotionLink = motion.create(Link);

const AVATARS = [
    "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop&crop=faces&q=80",
    "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop&crop=faces&q=80",
    "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&h=100&fit=crop&crop=faces&q=80",
    "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop&crop=faces&q=80",
];

const HERO_INTRO_VIDEO = '/videos/hero/hyperdrive-intro.mp4';
const HERO_LOOP_VIDEO = '/videos/hero/hyperdrive-loop.mp4';
const HERO_POSTER_IMAGE = '/videos/hero/hyperdrive-poster.jpg';

// --- Performance Tier Detection ---

/**
 * usePerformanceTier
 *
 * Detects device capability using:
 *  - `prefers-reduced-motion` (OS accessibility setting)
 *  - `navigator.hardwareConcurrency` (logical CPU cores)
 *  - `navigator.deviceMemory` (RAM in GB, Chrome-only)
 *  - `navigator.connection.saveData` / `effectiveType` (network hints)
 *
 * Returns `true` when the device is low-end and heavy effects should be skipped.
 * Defaults to `false` (full effects) during SSR and before hydration.
 */
function useIsLowEnd() {
    const prefersReduced = useReducedMotion();
    const [lowEnd, setLowEnd] = useState(false);

    useEffect(() => {
        if (prefersReduced) {
            setLowEnd(true);
            return;
        }

        const nav = /** @type {any} */ (navigator);
        const cores = nav.hardwareConcurrency || 4;
        const memory = nav.deviceMemory || 4; // GB — only available in Chromium
        const conn = nav.connection;
        const saveData = conn?.saveData ?? false;
        const slowNetwork = ['slow-2g', '2g'].includes(conn?.effectiveType);

        // Heuristic: ≤2 cores OR ≤2 GB RAM OR data-saver OR very slow connection
        if (cores <= 2 || memory <= 2 || saveData || slowNetwork) {
            setLowEnd(true);
        }
    }, [prefersReduced]);

    return lowEnd;
}

function useHeroVideoMode() {
    const prefersReduced = useReducedMotion();
    const [mode, setMode] = useState(null);

    useEffect(() => {
        if (prefersReduced) {
            setMode('static');
            return;
        }

        const nav = /** @type {any} */ (navigator);
        const conn = nav.connection;
        const cores = nav.hardwareConcurrency || 4;
        const memory = nav.deviceMemory || 4;
        const saveData = conn?.saveData ?? false;
        const effectiveType = conn?.effectiveType || '';
        const constrainedDevice = cores <= 2 || memory <= 2 || saveData || ['slow-2g', '2g'].includes(effectiveType);

        if (constrainedDevice) {
            setMode('static');
            return;
        }

        const mobileViewport = window.matchMedia('(max-width: 767px)').matches;
        const coarsePointer = window.matchMedia('(pointer: coarse)').matches;

        setMode(mobileViewport || coarsePointer ? 'static' : 'desktop');
    }, [prefersReduced]);

    return mode;
}

// --- Sub-components (memoized to prevent re-renders from parent state changes) ---

/**
 * AvatarTrustCluster
 * Social proof element with stacked avatars, star rating, and key metrics.
 */
const AvatarTrustCluster = memo(function AvatarTrustCluster({ isLowEnd }) {
    return (
        <motion.div
            {...(!isLowEnd && {
                initial: { opacity: 0, x: -20 },
                animate: { opacity: 1, x: 0 },
                transition: { duration: 0.8, ease: "easeOut", delay: 0.1 },
            })}
            className="flex flex-col items-start gap-2 mb-8 md:mb-10"
        >
            <div className="flex items-center gap-4">
                <div className="flex -space-x-4 pl-1">
                    {AVATARS.map((src, i) => (
                        <div
                            key={i}
                            className={cn(
                                "relative w-10 h-10 md:w-11 md:h-11 rounded-full border border-white/10 overflow-hidden ring-2 ring-[#030304] shadow-xl z-0",
                                !isLowEnd && "hover:z-10 transition-all duration-300"
                            )}
                        >
                            <Image
                                src={src}
                                alt=""
                                fill
                                sizes="44px"
                                loading="lazy"
                                className="object-cover grayscale-[0.3]"
                            />
                        </div>
                    ))}
                    <div
                        className={cn(
                            "relative w-10 h-10 md:w-11 md:h-11 rounded-full border border-white/10 flex items-center justify-center ring-2 ring-[#030304] text-[10px] md:text-xs font-semibold text-white shadow-xl z-20",
                            isLowEnd ? "bg-neutral-900" : "bg-neutral-900/90 backdrop-blur-sm"
                        )}
                    >
                        +40
                    </div>
                </div>

                <div className="flex flex-col justify-center pl-1">
                    <div className="flex gap-0.5 text-[#D4AF37] mb-1 drop-shadow-md">
                        {[...Array(5)].map((_, i) => (
                            <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
                        ))}
                    </div>
                    <p className="text-xs font-medium text-neutral-300 tracking-wide">
                        Trusted by 40+ Businesses
                    </p>
                </div>
            </div>
        </motion.div>
    );
});

/**
 * StatsBar
 * Horizontal metrics strip — reinforces credibility with concrete numbers.
 * Modeled after Halo Lab's awards/stats strip for immediate trust signaling.
 */
const STATS = [
    { value: "100+", label: "Projects Shipped", icon: Zap },
    { value: "4.9★", label: "Client Rating", icon: Star },
    { value: "24hr", label: "Response Time", icon: Clock },
    { value: "100%", label: "NDA Protected", icon: ShieldCheck },
];

const StatsBar = memo(function StatsBar({ isLowEnd }) {
    return (
        <motion.div
            {...(!isLowEnd && {
                initial: { opacity: 0, y: 20 },
                animate: { opacity: 1, y: 0 },
                transition: { duration: 0.8, delay: 0.9 },
            })}
            className="mt-14 w-full max-w-3xl mx-auto"
        >
            <div className={cn(
                "grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 rounded-2xl border border-white/[0.06] px-6 py-5",
                isLowEnd ? "bg-neutral-900/60" : "bg-white/[0.03] backdrop-blur-sm"
            )}>
                {STATS.map(({ value, label, icon: Icon }) => (
                    <div key={label} className="flex items-center gap-3">
                        <Icon size={18} className="text-[#D4AF37] shrink-0" strokeWidth={1.5} />
                        <div className="flex flex-col">
                            <span className="text-sm md:text-base font-semibold text-white tracking-tight">{value}</span>
                            <span className="text-[10px] md:text-xs text-neutral-400 tracking-wide">{label}</span>
                        </div>
                    </div>
                ))}
            </div>
        </motion.div>
    );
});

const HyperdriveBackdrop = memo(function HyperdriveBackdrop({ isLowEnd }) {
    // The poster IS the LCP image. Its high-priority preload <link> lives in
    // the page server component (app/(site)/page.jsx) — a ReactDOM.preload
    // call here does not survive static prerender.
    const [isLooping, setIsLooping] = useState(false);
    const [canStartVideo, setCanStartVideo] = useState(false);
    const [introReady, setIntroReady] = useState(false);
    const [introEnded, setIntroEnded] = useState(false);
    const [loopReady, setLoopReady] = useState(false);
    const [videoFailed, setVideoFailed] = useState(false);
    const introRef = useRef(null);
    const loopRef = useRef(null);
    const videoMode = useHeroVideoMode();
    const shouldPlayVideo = videoMode === 'desktop' && canStartVideo;
    const canUseVideo = shouldPlayVideo && !videoFailed;
    const showPoster = !canUseVideo || !introReady;
    const showIntro = canUseVideo && introReady && !isLooping;
    const showLoop = canUseVideo && loopReady && isLooping;

    useEffect(() => {
        if (videoMode !== 'desktop') {
            setCanStartVideo(false);
            return;
        }

        let timeoutId;
        let idleId;
        const startWhenIdle = () => {
            if ('requestIdleCallback' in window) {
                idleId = window.requestIdleCallback(() => setCanStartVideo(true), { timeout: 2500 });
            } else {
                timeoutId = window.setTimeout(() => setCanStartVideo(true), 1800);
            }
        };

        if (document.readyState === 'complete') {
            timeoutId = window.setTimeout(startWhenIdle, 800);
        } else {
            window.addEventListener('load', startWhenIdle, { once: true });
        }

        return () => {
            window.removeEventListener('load', startWhenIdle);
            window.clearTimeout(timeoutId);
            if (idleId) window.cancelIdleCallback(idleId);
        };
    }, [videoMode]);

    useEffect(() => {
        if (!shouldPlayVideo) {
            setIntroReady(false);
            setIntroEnded(false);
            setLoopReady(false);
            setIsLooping(false);
            setVideoFailed(false);
        }
    }, [shouldPlayVideo]);

    useEffect(() => {
        if (!shouldPlayVideo) return;

        const introVideo = introRef.current;
        if (!introVideo) return;

        introVideo.currentTime = 0;
        introVideo.play().catch(() => {
            setVideoFailed(true);
        });
    }, [shouldPlayVideo]);

    useEffect(() => {
        if (!canUseVideo || !introEnded || !loopReady) return;

        setIsLooping(true);
    }, [canUseVideo, introEnded, loopReady]);

    useEffect(() => {
        if (!canUseVideo || !isLooping) return;

        const loopVideo = loopRef.current;
        if (!loopVideo) return;

        loopVideo.currentTime = 0;
        loopVideo.play().catch(() => {
            setVideoFailed(true);
        });
    }, [canUseVideo, isLooping]);

    return (
        <div className="absolute inset-0 z-0 overflow-hidden" aria-hidden="true">
            <img
                src={HERO_POSTER_IMAGE}
                alt=""
                className={cn(
                    "absolute inset-0 h-full w-full object-cover transition-opacity duration-700",
                    showPoster ? (isLowEnd ? "opacity-50" : "opacity-65") : "opacity-0"
                )}
                loading="eager"
                fetchPriority="high"
            />
            {shouldPlayVideo && !videoFailed && (
                <video
                    ref={introRef}
                    className={cn(
                        "absolute inset-0 h-full w-full object-cover transition-opacity duration-700",
                        showIntro ? (isLowEnd ? "opacity-50" : "opacity-65") : "opacity-0"
                    )}
                    autoPlay
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedData={() => setIntroReady(true)}
                    onPlaying={() => setIntroReady(true)}
                    onEnded={() => setIntroEnded(true)}
                    onError={() => setVideoFailed(true)}
                >
                    <source src={HERO_INTRO_VIDEO} type="video/mp4" />
                </video>
            )}
            {shouldPlayVideo && !videoFailed && (
                <video
                    ref={loopRef}
                    className={cn(
                        "absolute inset-0 h-full w-full object-cover transition-opacity duration-700",
                        showLoop ? (isLowEnd ? "opacity-50" : "opacity-65") : "opacity-0"
                    )}
                    muted
                    loop
                    playsInline
                    preload="auto"
                    onLoadedData={() => setLoopReady(true)}
                    onCanPlay={() => setLoopReady(true)}
                    onError={() => setVideoFailed(true)}
                >
                    <source src={HERO_LOOP_VIDEO} type="video/mp4" />
                </video>
            )}
            <div className="absolute inset-0 bg-[#030304]/55" />
            <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-[#030304] to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#030304] via-[#030304]/80 to-transparent" />
            <div className="absolute left-1/2 top-1/2 h-[34rem] w-[34rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#D4AF37]/10 blur-3xl" />
        </div>
    );
});

/**
 * GlassButton
 * Premium glassmorphism CTA with metallic rim effects.
 *
 * Low-end: drops backdrop-blur, shine animation, FlipText hover, and scale transform.
 */
const GlassButton = memo(function GlassButton({
    href,
    children,
    mobileText,
    variant = 'primary',
    icon: Icon,
    hoverText,
    isLowEnd,
}) {
    const isPrimary = variant === 'primary';

    return (
        <MotionLink
            href={href}
            initial="initial"
            whileHover={isLowEnd ? undefined : "hover"}
            className="group relative pointer-events-auto inline-block"
        >
            <div className={cn(
                "relative overflow-hidden rounded-full px-8 py-4 flex items-center gap-3",
                !isLowEnd && "transition-all duration-300 transform group-hover:scale-[1.02]",
                isPrimary
                    ? cn(
                        "border border-white/10 shadow-[0_0_30px_-10px_rgba(212,175,55,0.15)]",
                        isLowEnd ? "bg-neutral-900" : "bg-white/5 backdrop-blur-md"
                    )
                    : cn(
                        "bg-transparent text-white/70 border border-transparent",
                        !isLowEnd && "hover:text-white hover:bg-white/5"
                    )
            )}>
                {/* Shine sweep — skipped on low-end (GPU-composited gradient slide) */}
                {isPrimary && !isLowEnd && (
                    <motion.div
                        className="absolute inset-0 z-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                        variants={{
                            initial: { x: "-100%", transition: { duration: 0 } },
                            hover: {
                                x: "100%",
                                transition: { duration: 1.5, ease: "easeInOut", repeat: 0 },
                            },
                        }}
                    />
                )}

                {/* Text — FlipText requires hover variants, skip on low-end */}
                <span className={cn(
                    "relative z-10 font-semibold tracking-wide text-sm uppercase",
                    isPrimary ? "text-white" : "currentColor"
                )}>
                    {mobileText ? (
                        <span className="md:hidden">{mobileText}</span>
                    ) : null}
                    {hoverText && !isLowEnd ? (
                        <span className={mobileText ? "hidden md:inline-block" : undefined}>
                            <FlipText frontText={children} backText={hoverText} className="h-5" />
                        </span>
                    ) : (
                        <span className={mobileText ? "hidden md:inline" : undefined}>
                            {children}
                        </span>
                    )}
                </span>

                {Icon && (
                    <Icon
                        size={18}
                        className={cn(
                            "relative z-10",
                            !isLowEnd && "transition-transform group-hover:translate-x-1",
                            isPrimary ? "text-[#D4AF37]" : "currentColor"
                        )}
                    />
                )}

                {/* Metallic rim highlights */}
                {isPrimary && (
                    <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent opacity-60" />
                )}
                {isPrimary && (
                    <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-60" />
                )}
            </div>
        </MotionLink>
    );
});

// --- Main Hero Component ---

const HeroSectionOptimized = () => {
    const isLowEnd = useIsLowEnd();
    // Same gate as the hero video: the WebGL shader is desktop-only. On
    // mobile this keeps three.js out of the download/hydration path entirely.
    const videoMode = useHeroVideoMode();

    return (
        <div className="bg-[#f0efec] p-2">
            <section className="relative w-full min-h-[calc(100vh-1rem)] flex flex-col items-center justify-center overflow-hidden rounded-3xl bg-[#030304] text-white pt-28 pb-12 px-6 ring-1 ring-black/10">
                <HyperdriveBackdrop isLowEnd={isLowEnd} />
                {/* WebGL shader, masked inside the rounded card and layered over the video. */}
                {!isLowEnd && videoMode === 'desktop' && (
                    <Mosaic
                        width="100%"
                        height="100%"
                        variant="shader"
                        color1="#3b70b4"
                        color2="#d7d10d"
                        color3="#2bc4ff"
                        speed={0.25}
                        pixelSize={14}
                        borderIntensity={0.9}
                        waveIntensity={1.5}
                        waveWidth={2}
                        opacity={0.8}
                        className="absolute inset-0 z-0 mix-blend-screen"
                    />
                )}

            {/* Main Content Layout */}
            <div
                className="relative z-10 w-full max-w-7xl flex flex-col md:items-start lg:items-center text-center lg:text-center mx-auto"
            >
                <div className="w-full flex flex-col items-start lg:items-center">
                    {/* 1. Top Avatar Cluster */}
                    <div className="px-1 w-full flex justify-start lg:justify-center lg:-ml-[38rem] xl:-ml-[42rem] relative">
                        <AvatarTrustCluster isLowEnd={isLowEnd} />
                    </div>

                    {/* Eyebrow */}
                    <motion.div
                        {...(!isLowEnd && {
                            initial: { opacity: 0, y: -10 },
                            animate: { opacity: 1, y: 0 },
                            transition: { duration: 0.8, delay: 0.2 },
                        })}
                        className="self-center text-center font-mono font-normal text-[0.72rem] tracking-[0.34em] uppercase text-kingdom-gold mb-6"
                    >
                        Design-Driven · AI-Powered
                    </motion.div>

                    {/* 2. Headline — Outcome-driven, Kingdom Codes positioning */}
                    <h1
                        className="font-next-sphere text-4xl sm:text-5xl md:text-6xl lg:text-[5.2rem] font-extrabold uppercase tracking-tight leading-[0.95] text-white opacity-80 max-w-6xl mx-auto [text-shadow:0_4px_80px_rgba(255,255,255,0.15),0_10px_60px_rgba(0,0,0,0.8)]"
                    >
                        We Build Revenue <br className="hidden md:block" />
                        Engines
                    </h1>

                    {/* 3. Subtext — Value equation: Dream Outcome + Low Time/Effort */}
                    <p
                        className="mt-6 md:mt-8 max-w-2xl text-base md:text-xl font-light text-white/90 leading-relaxed mx-auto [text-shadow:0_2px_4px_rgba(0,0,0,0.8),0_8px_32px_rgba(0,0,0,0.8)]"
                    >
                        You built something real. We make sure the technology behind it works as hard as you do — accelerating operations, cutting costs, and unlocking revenue in weeks, not months. You stay on the business. We carry everything else.
                    </p>

                    {/* 4. CTAs — Tiered intent: lead magnet (primary) + social proof (secondary) */}
                    <div
                        className="mt-12 w-full flex flex-col items-center gap-6"
                    >
                        <div className="flex flex-wrap items-center justify-center gap-6">
                            <PixelButton href="/contact" glyph="k">
                                <span className="md:hidden">
                                    Unlock Your Growth Code — Free
                                </span>
                                <span className="hidden md:inline">
                                    Unlock Your Growth Code
                                </span>
                            </PixelButton>
                            <GlassButton href="/work" variant="secondary" icon={PlayCircle} isLowEnd={isLowEnd}>
                                See the Proof
                            </GlassButton>
                        </div>
                        <p
                            className="text-xs text-neutral-400 tracking-wide flex items-center gap-2"
                        >
                            <Clock size={12} className="text-[#D4AF37]" />
                            Free strategy audit — results delivered within 24 hours
                        </p>
                    </div>

                    {/* 5. Stats Bar — concrete metrics for trust */}
                    <StatsBar isLowEnd={isLowEnd} />
                </div>
            </div>

            {/* 6. Scroll Indicator */}
            <motion.div
                className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-neutral-400 pointer-events-none z-10"
                initial={{ opacity: 0 }}
                animate={
                    isLowEnd
                        ? { opacity: 1 }
                        : { opacity: 1, y: [0, 10, 0] }
                }
                transition={
                    isLowEnd
                        ? { duration: 0.5 }
                        : {
                            opacity: { delay: 1.5, duration: 1 },
                            y: { duration: 2, repeat: Infinity, ease: "easeInOut" },
                        }
                }
            >
                <div className="w-[1px] h-8 bg-gradient-to-b from-transparent to-neutral-500/50" />
                <p className="text-[10px] uppercase tracking-[0.2em]">Scroll</p>
            </motion.div>

            </section>
        </div>
    );
};

HeroSectionOptimized.displayName = "HeroSectionOptimized";
export default HeroSectionOptimized;
